import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
  Tabs,
  Tab,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, CompareArrows } from '@mui/icons-material';
import ProductCard from '../components/Insurance/ProductCard';

const InsuranceResults = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { userProfile, matches, allResults } = location.state || {};

  const [activeTab, setActiveTab] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortBy, setSortBy] = useState('match_score');

  if (!userProfile || !matches) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          No results found. Please start a new insurance search.
        </Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Go to Homepage
        </Button>
      </Container>
    );
  }

  const allProducts = allResults ?? matches;
  const displayProducts = activeTab === 0 ? matches : allProducts;

  const sortedProducts = [...displayProducts].sort((a, b) => {
    const premiumA = a.personalized_premium ?? a.base_premium ?? 0;
    const premiumB = b.personalized_premium ?? b.base_premium ?? 0;
    switch (sortBy) {
      case 'price_low':
        return premiumA - premiumB;
      case 'price_high':
        return premiumB - premiumA;
      case 'rating':
        return (b.avg_review_rating || 0) - (a.avg_review_rating || 0);
      case 'savings':
        return (b.savings?.amount || 0) - (a.savings?.amount || 0);
      case 'match_score':
      default:
        return (b.match_score || 0) - (a.match_score || 0);
    }
  });

  const handleBuyNow = (product) => {
    navigate('/checkout', {
      state: {
        product,
        userProfile
      }
    });
  };

  const handleCompare = (product) => {
    setSelectedProducts((prev) => {
      if (prev.find((p) => p.id === product.id)) {
        return prev.filter((p) => p.id !== product.id);
      }
      if (prev.length < 3) {
        return [...prev, product];
      }
      alert('You can compare up to 3 products at once');
      return prev;
    });
  };

  const handleViewDetails = (product) => {
    navigate(`/product/${product.id}`, {
      state: {
        product,
        userProfile
      }
    });
  };

  const handleGoToComparison = () => {
    navigate('/compare', {
      state: {
        products: selectedProducts,
        userProfile
      }
    });
  };

  const totalSavings = matches.reduce((sum, p) => sum + (p.savings?.amount || 0), 0);
  const avgSavings = matches.length > 0 ? Math.round(totalSavings / matches.length) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        Back
      </Button>

      {/* Summary Banner */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'primary.light' }}>
        <Typography variant="h4" gutterBottom color="primary.dark">
          🎉 {userProfile.is_switching ? 'Great News!' : 'Perfect Matches Found!'}
        </Typography>

        {userProfile.is_switching && avgSavings > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" color="success.dark">
              You Could Save ${avgSavings}/year
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Based on your current premium of ${userProfile.current_premium}/year
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip label={`Found ${allProducts.length} plans`} />
          <Chip label={`Family of ${userProfile.family_size}`} />
          <Chip label={`${userProfile.coverage_level || '—'} coverage`} />
          {userProfile.budget_monthly != null && (
            <Chip label={`~$${userProfile.budget_monthly}/month budget`} />
          )}
        </Stack>
      </Paper>

      {/* Tabs and Sorting */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`Top Matches (${matches.length})`} />
          <Tab label={`All Results (${allProducts.length})`} />
        </Tabs>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="match_score">Best Match</MenuItem>
            <MenuItem value="price_low">Price: Low to High</MenuItem>
            <MenuItem value="price_high">Price: High to Low</MenuItem>
            <MenuItem value="rating">Highest Rated</MenuItem>
            <MenuItem value="savings">Most Savings</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Comparison Bar */}
      {selectedProducts.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 80,
            zIndex: 100,
            p: 2,
            mb: 3,
            bgcolor: 'secondary.light'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body1">
              {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected for comparison
            </Typography>
            <Button
              variant="contained"
              startIcon={<CompareArrows />}
              onClick={handleGoToComparison}
              disabled={selectedProducts.length < 2}
            >
              Compare {selectedProducts.length > 1 ? `(${selectedProducts.length})` : ''}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Products Grid */}
      <Grid container spacing={3}>
        {sortedProducts.map((product, index) => (
          <Grid item xs={12} md={4} key={product.id}>
            {index === 0 && activeTab === 0 && (
              <Chip label="⭐ BEST MATCH" color="success" sx={{ mb: 1, fontWeight: 'bold' }} />
            )}
            <ProductCard
              product={product}
              onBuyNow={handleBuyNow}
              onCompare={handleCompare}
              onViewDetails={handleViewDetails}
              showSavings={userProfile.is_switching}
              matchScore={product.match_score}
            />
          </Grid>
        ))}
      </Grid>

      {sortedProducts.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No products match your criteria. Try adjusting your preferences.
        </Alert>
      )}
    </Container>
  );
};

export default InsuranceResults;
